import React, {useEffect, useState} from "react";
import {Switch, Route, BrowserRouter} from "react-router-dom";
import {notification, ConfigProvider} from "antd";
import ruRu from 'antd/es/locale/ru_RU'
import enUS from 'antd/es/locale/en_US';
import zhCN from 'antd/es/locale/zh_CN';
import moment from "moment";
import "moment/locale/ru";
import 'moment/locale/zh-cn';
import "@assets/style.less";

import {mainConfig} from "@config";
import {AppContext} from "@contexts/app-context";

import Header from "@components/header";
import Content from "@components/content";
import MainFooter from "@components/main-page-blocks/footer";
import Error from "@components/error";
import PrivateRoute from "@components/routers/private-router";
import ModalCreateNewLegalEntity from "@modals/create-legal-entity";
import HomePage from "@pages/home-page";
import LoginPage from "@pages/login-page";
import ResetPage from "@pages/reset-page";
import RegisterPage from "@pages/register-page";
import CarrierNewDeliveries from "@pages/deliveries/carrier/new-deliveries";
import CarrierBookedDeliveries from "@pages/deliveries/carrier/booked-deliveries";
import CarrierArchivedDeliveries from "@pages/deliveries/carrier/archived-deliveries";
import LogistFreeDeliveries from "@pages/deliveries/logist/free-deliveries";
import LogistReservedDeliveries from "@pages/deliveries/logist/reserved-deliveries";
import LogistBookedDeliveries from "@pages/deliveries/logist/booked-deliveries";
import LogistArchivedDeliveries from "@pages/deliveries/logist/archived-deliveries";
import DriverNewDeliveries from "@pages/deliveries/driver/new-deliveries";
import LogistOnLoadingDeliveries from "@pages/deliveries/logist/on-load-deliveries";
import LogistEnRouteDeliveries from "@pages/deliveries/logist/enroute-deliveries";
import CarrierOnLoadingDeliveries from "@pages/deliveries/carrier/on-load-deliveries";
import CarrierEnRouteDeliveries from "@pages/deliveries/carrier/enroute-deliveries";
import {userServices} from "@services";
import SettingsPage from "@pages/settings-page";
import ReportsPage from "@pages/reports-page";
import GuestNewDeliveries from "@pages/deliveries/guest/free-deliveries";
import ConsignorPage from "@pages/consignor-page";
import CompanyPage from "@pages/company-page";
import TermsOfUse from "@pages/terms-of-use";
import ServicePage from "@pages/service-page";
import ReturnDeliveries from "@pages/deliveries/carrier/return-deliveries";
import DriverPage from "@pages/driver-page";
import StartPage from "@pages/start-page";
import PrivacyPage from "@pages/privacy";
import {LANG_ENUM, LANGS} from "../../helpers/lang.helper";
import i18n from "../../i18n";
import Cookies from "js-cookie";
import AdsBanner from "../ads-banner";
import LoadingUnloadingPage from "../pages/loading-unloading";
import ModalLegalEntityAgreement from "../modals/legal-entity-agreement";
import BuyerPage from "../pages/unloadings/buyer/actual-unloadings/index";
import BuyerArchivedUnloadings from "../pages/unloadings/buyer/archived-unloadings";
import {getTranslatedConstant} from "../../helpers/translate.helpers";
import CrossDockPage from "../pages/cross-dock";

notification.config({
  placement: "bottomRight",
  bottom: 25,
  duration: 5,
});

export default function App() {
  document.title = mainConfig.companyName;
  document.querySelector('#root').className = window.location.pathname !== '/' ? 'theme' : 'main-page';

  const [user, setUser] = useState(null);
  const [needUpdateUser, setNeedUpdateUser] = useState(false);

  const [showModalCreateEntity, setShowModalCreateEntity] = useState(false);
  const [showModalLegalEntityAgreement, setShowModalLegalEntityAgreement] = useState(false);

  
  const updateUser = (callback = null, isNeedSetUser = true) => {
    userServices.updateUserInfo()
      .then(() => {
        let userData = userServices.getUser();

        if (isNeedSetUser) {
          setUser(userData);
        }

        if (callback instanceof Function) {
          callback(userData);
        }
      });
  };

  /**
   * Автообновление информации о пользователе
   * Каждую минуту
   */
  useEffect(() => {
    updateUser();

    const updateInterval = setInterval(() => updateUser(null, false), 1000 * 600);
    return () => clearInterval(updateInterval);
  }, []);

  useEffect(() => {
    if (needUpdateUser) {
      updateUser((userData) => {
        if (userData.info.isStaff) {
          // history.push("/deliveries/list-free");
          window.location.href = "/deliveries/list-free";
        } else {
          // history.push("/deliveries/list-new");
          window.location.href = "/deliveries/list-new";
        }
      }, true);
      setNeedUpdateUser(false);
    }
  }, [needUpdateUser]);

  useEffect(() => {
    if (user && user.loggedIn) {
      if (user.info.rules.IsNeedCreateLegalEntity) {
        setShowModalLegalEntityAgreement(true);
      }
    }
  }, [user])

  const AppProvider = (props) => {
    const userData = userServices.getUser();

    let defaultSort = getTranslatedConstant('defaultSort');

    const [userStore, setUserStore] = useState({
      ...userData,
    });

    const [currentPath, setCurrentPath] = useState(null);

    const lang = Cookies.get('language')? Cookies.get('language') :  LANG_ENUM.RU;
    const langObj = {
      [LANG_ENUM.RU]: ruRu,
      [LANG_ENUM.EN]: enUS,
      [LANG_ENUM.CN]: zhCN,
    }
    i18n.changeLanguage(lang);

    useEffect(() => {
      defaultSort = getTranslatedConstant('defaultSort');
      setFilterStore({
        ...filterStore,
        selectedSorting: defaultSort[0].name,
        sort: defaultSort,
      })
    }, [lang])

    const [langStore, setLangStore] = useState({
      lang: lang,
      locale: LANGS.filter((item) => item.key === lang)[0].locale,
      langObj: langObj[lang]
    });

    const [filterStore, setFilterStore] = useState({
      enable: false,
      selectedRegionsLoading: {},
      selectedRegionsUnloading: {},
      selectedSorting: defaultSort[0].name,
      regionsLoadings: [],
      regionsUnloadings: [],
      sort: defaultSort,
      selectedConsignors: [],
      excludedConsignors: [],
      isExcludeConsignors: false
    });

    const [isNeedUpdateUnloadingList, setNeedUpdateUnloadingList] = useState(false);


    const stores = {
      currentPath: currentPath,
      user: userStore,
      filter: {
        ...filterStore,
      },
      lang: {...langStore},
      isNeedUpdateUnloadingList: isNeedUpdateUnloadingList
    };

    const appStoreProvider = {
      ...stores,
      applyFilterParams: (selectedRegionsLoading, selectedRegionsUnloading, selectedSorting, selectedConsignors, excludedConsignors, isExcludeConsignors) => {
        setFilterStore({
          ...filterStore,
          enable: true,
          selectedRegionsLoading: selectedRegionsLoading,
          selectedRegionsUnloading: selectedRegionsUnloading,
          selectedSorting: selectedSorting,
          selectedConsignors: selectedConsignors,
          excludedConsignors: excludedConsignors,
          isExcludeConsignors: isExcludeConsignors
        });
      },
      applySearchString: (searchString) => {
        setFilterStore({
          ...filterStore,
          enableSearch: true,
          searchString: searchString
        });
      },
      initFilter: (listSort = defaultSort) => {
        setFilterStore({
          ...filterStore,
          sort: listSort,
        });
      },
      enableFilter: (regionsLoadings, regionsUnloadings) => {
        setFilterStore({
          ...filterStore,
          enable: true,
          regionsLoadings: regionsLoadings,
          regionsUnloadings: regionsUnloadings,
        });
      },
      enableSearchFilter: () => {
        setFilterStore({
          ...filterStore,
          enableSearch: true,
        });
      },
      disableFilter: () => {
        setFilterStore({
          ...filterStore,
          enable: false,
          regionsLoadings: [],
          regionsUnloadings: [],
          selectedRegionsLoading: [],
          selectedRegionsUnloading: [],
          selectedSorting: defaultSort[0].name,
          selectedConsignors: [],
          excludedConsignors: [],
          isExcludeConsignors: false
        });
      },
      login: async (username, password) => {
        return await userServices.login(username, password).then((resUserData) => {
          const objJsonStr = JSON.stringify(resUserData);
          const objJsonB64 = Buffer.from(objJsonStr).toString("base64");

          localStorage.setItem("user_auth_v2", objJsonB64);

          const userData = userServices.getUser();
          setUserStore({...userData});

          updateUser();
        });
      },
      logout: () => {
        userServices.logout().then(() => {
          const userData = userServices.getUser();

          setFilterStore({...filterStore, enable: false});
          setUserStore({...userData});

          updateUser();
        });
      },
      reset: async (username) => {
        return await userServices.resetPass(username).then((data) => {
          return username;
        });
      },
      setLang: (lang) => {
        i18n.changeLanguage(lang);
        const langObjNew = langObj[lang];
        if (!lang) {
          moment.locale('ru-ru');
        }
        if(lang===LANG_ENUM.CN) {
          moment.locale('zh-cn');
        }
        if(lang===LANG_ENUM.EN) {
          moment.locale('en');
        }
        Cookies.set('language', lang, {expires: 7000})
        setLangStore({
          lang: lang,
          locale: LANGS.filter((item) => item.key === lang)[0].locale,
          langObj: langObjNew,
        })
      },
      setIsNeedUpdateUnloadingList: (value) => {
        setNeedUpdateUnloadingList(value);
      },
      setCurrentPath: (path) => {
        setCurrentPath(path)
      }
    };

    return <AppContext.Provider value={appStoreProvider}>
      <ConfigProvider locale={langStore.langObj}>{props.children}
      </ConfigProvider></AppContext.Provider>;
  };

  return (
    <AppProvider>
      <BrowserRouter>
        <div className="wrapper">
          {(window.location.pathname !== '/') &&
            <Header user={user} setNeedUpdateUser={setNeedUpdateUser}/>
          }
          <Switch>
            <Route path="/" exact component={HomePage}/>
            <Route path="/login" component={LoginPage} user={user}/>
            <Route path="/register" component={RegisterPage}/>
            <Route path="/reset" component={ResetPage}/>
            <Route path="/terms-of-use" component={TermsOfUse}/>
            <Route path="/privacy-page" component={PrivacyPage}/>

            <Route path="/deliveries/dashboard-deliveries">
              <Content>
                <GuestNewDeliveries/>
              </Content>
            </Route>

            <PrivateRoute path="/deliveries/list-new" component={<CarrierNewDeliveries/>}/>
            <PrivateRoute path="/deliveries/return-deliveries/:id" component={<ReturnDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-my" component={<CarrierBookedDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-archived-carrier" component={<CarrierArchivedDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-onload-carrier" component={<CarrierOnLoadingDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-enroute-carrier" component={<CarrierEnRouteDeliveries/>}/>

            <PrivateRoute path="/deliveries/list-free" component={<LogistFreeDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-reserve" component={<LogistReservedDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-booked" component={<LogistBookedDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-archived-logist" component={<LogistArchivedDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-onload-logist" component={<LogistOnLoadingDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-enroute-logist" component={<LogistEnRouteDeliveries/>}/>
            <PrivateRoute path="/unloadings/list-actual" component={<BuyerPage/>}/>
            <PrivateRoute path="/unloadings/list-archived" component={<BuyerArchivedUnloadings/>}/>

            <PrivateRoute path="/loading-unloading/" component={<LoadingUnloadingPage/>}/>

            <PrivateRoute path="/cross-dock/list-active" component={<CrossDockPage type="active" />}/>
            <PrivateRoute path="/cross-dock/list-archive" component={<CrossDockPage type="archive" />}/>

            <PrivateRoute path="/deliveries/list-new-driver" component={<DriverNewDeliveries/>}/>
            <PrivateRoute path="/settings" component={<SettingsPage/>}/>
            <PrivateRoute path="/reports" component={<ReportsPage/>} aside={true}/>
            <PrivateRoute path="/driver" component={<DriverPage/>}/>

            <PrivateRoute path="/company" component={<CompanyPage/>}/>
            <PrivateRoute path="/consignor" component={<ConsignorPage/>}/>
            <PrivateRoute path="/service" component={<ServicePage/>}/>

            <PrivateRoute path="/start" component={<StartPage/>}/>

            <Route render={() => <Content><Error status={404} desc={i18n.t('main.status.404')}/></Content>}/>
          </Switch>
          {(window.location.pathname !== '/') &&
            <MainFooter mod={"dark"}/>
          }
        </div>
        {/*<AdsBanner type={"1"}/>*/}
        <ModalLegalEntityAgreement
          isShow={showModalLegalEntityAgreement}
          setIsShow={setShowModalLegalEntityAgreement}
          onAccepted={() => setShowModalCreateEntity(true)}
        />
        <ModalCreateNewLegalEntity
          isShow={showModalCreateEntity}
          setIsShow={setShowModalCreateEntity}
          setLoadingList={() => {
          }}
          setStopListUpdate={() => {
          }}
        />
      </BrowserRouter>
    </AppProvider>
  );
}
